/**
 * «Utskrifter»: the Ramadan calendar and the month's prayer timetable as a table to print.  MKK #34, owner 2026-10-29.
 *
 * Staff read views over data the mosque already has — its timetable (prayer_day, with the jamaat periods and the
 * per-day overrides applied by the server), and its Hijri calendar (tabular, shifted by the mosque's moon-sighting
 * adjustments). Nothing is stored and nothing is calculated that the timetable does not hold: a day without a row is a
 * row of dashes, never an invented time (CA-20: there is no calculation engine in this platform).
 *
 * One builder for both outputs: the server turns document() into an A4 PDF (apps/api/src/table-pdf.js, WinAnsi only,
 * so Latin words), and the panel draws the very same rows as an HTML page to print, where the browser's fonts may add
 * Arabic and Urdu headings (EXTRA_HEADINGS) — never in the PDF.
 *
 * Pure: no db, no clock, no I/O. Dates are bare `YYYY-MM-DD` strings and calendar arithmetic is done in UTC on them,
 * so the answer is the same in every time zone the spec runs in (npm run spec:tz). Prayer times are the timetable's
 * own wall-clock strings for the mosque's zone; daylight saving is already in them (Europe/Oslo writes 06:12 the day
 * after the clocks change, not 07:12), so nothing here adds or subtracts hours.
 *
 * No personal data: a printout names the mosque and its public details only.
 */
'use strict';
const H = require('./hijri');

const PRAYERS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
const DASH = '–';
/** The longest span one printout covers (a Ramadan is 29–30 days; a month at most 31). */
const MAX_DAYS = 62;
const KINDS = ['ramadan', 'month'];
const LANGS = ['no', 'en'];

const LABELS = {
  no: {
    ramadanTitle: (hy, gy) => `Ramadan-kalender ${hy} (${gy})`,
    monthTitle: (name, y) => `Bønnetider ${name} ${y}`,
    months: ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'],
    weekdays: ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'],
    col: { rday: 'Dag', date: 'Dato', weekday: 'Ukedag', hijri: 'Hijri', sehri: 'Sehri slutt', fajr: 'Fajr', sunrise: 'Soloppgang',
           dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', iftar: 'Iftar', isha: 'Isha' },
    jamaatNote: 'Klokkeslett = når bønnen begynner; i parentes = jamaat der moskeen har satt det.',
    sehriNote: 'Sehri slutter når Fajr begynner — ikke ved jamaat. Iftar er ved Maghrib.',
    missingNote: `${DASH} = ingen tid i moskeens bønnetidstabell for den dagen.`,
    closedNote: 'Stengt = moskeen har meldt dagen som stengt.',
    hijriNote: 'Hijri-datoene følger moskeens kalender; månesikting kan flytte Ramadan én dag.',
    closed: 'Stengt',
    source: (name, url) => `Bønnetidene er fra ${name} (${url}).`,
    printed: (d) => `Skrevet ut ${d}`,
  },
  en: {
    ramadanTitle: (hy, gy) => `Ramadan calendar ${hy} AH (${gy})`,
    monthTitle: (name, y) => `Prayer times ${name} ${y}`,
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    col: { rday: 'Day', date: 'Date', weekday: 'Weekday', hijri: 'Hijri', sehri: 'Sehri ends', fajr: 'Fajr', sunrise: 'Sunrise',
           dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', iftar: 'Iftar', isha: 'Isha' },
    jamaatNote: 'Time = when the prayer begins; in brackets = jamaat where the mosque has set one.',
    sehriNote: 'Sehri ends when Fajr begins — not at the jamaat. Iftar is at Maghrib.',
    missingNote: `${DASH} = no time in the mosque's timetable for that day.`,
    closedNote: 'Closed = the mosque has marked the day as closed.',
    hijriNote: "Hijri dates follow the mosque's calendar; moon sighting may move Ramadan by a day.",
    closed: 'Closed',
    source: (name, url) => `Prayer times from ${name} (${url}).`,
    printed: (d) => `Printed ${d}`,
  },
};

/**
 * Column headings in Arabic and Urdu, for the panel's HTML page ONLY (the browser has the fonts; the PDF is WinAnsi and
 * never carries them). Standard terms; the imam should read them once before a printout goes on the wall.
 */
const EXTRA_HEADINGS = {
  ar: { ramadanTitle: 'إمساكية رمضان', monthTitle: 'مواقيت الصلاة', sehri: 'السحور', fajr: 'الفجر', sunrise: 'الشروق', dhuhr: 'الظهر',
        asr: 'العصر', maghrib: 'المغرب', iftar: 'الإفطار', isha: 'العشاء' },
  ur: { ramadanTitle: 'رمضان المبارک کا نظام الاوقات', monthTitle: 'نماز کے اوقات', sehri: 'سحری', fajr: 'فجر', sunrise: 'طلوع آفتاب', dhuhr: 'ظہر',
        asr: 'عصر', maghrib: 'مغرب', iftar: 'افطار', isha: 'عشاء' },
};

/* ---- calendar arithmetic on bare dates (UTC, so no zone can move a day) ---- */
const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || '')) && !Number.isNaN(Date.parse(String(s) + 'T00:00:00Z'))
  && new Date(String(s) + 'T00:00:00Z').toISOString().slice(0, 10) === String(s);
const utc = (s) => { const [y, m, d] = String(s).split('-').map(Number); return Date.UTC(y, m - 1, d); };
const fromUtc = (ms) => new Date(ms).toISOString().slice(0, 10);
const addDays = (s, n) => fromUtc(utc(s) + n * 86400000);
/** 0 = Monday … 6 = Sunday (core rule 7: weeks start on Monday). */
const weekdayOf = (s) => (new Date(utc(s)).getUTCDay() + 6) % 7;

/** The days from `from` to `to`, both included; [] for a bad or reversed range, at most MAX_DAYS. */
function daysBetween(from, to, max = MAX_DAYS) {
  if (!isDate(from) || !isDate(to) || to < from) return [];
  const out = [];
  for (let d = from; d <= to && out.length < max; d = addDays(d, 1)) out.push(d);
  return out;
}

/** Every day of a Gregorian month, or [] for a month that is not one. */
function monthDays(year, month) {
  const y = Number(year), m = Number(month);
  if (!Number.isInteger(y) || y < 1900 || y > 2200 || !Number.isInteger(m) || m < 1 || m > 12) return [];
  const first = `${y}-${String(m).padStart(2, '0')}-01`;
  const last = fromUtc(Date.UTC(y, m, 0));
  return daysBetween(first, last);
}

/**
 * The Hijri date of a bare date with the mosque's offset in force that day. `offsetOf(date)` is the caller's (the
 * server passes hijri.activeOffset over the mosque's adjustments). The Date handed to toHijri is built from the parts,
 * at local noon, so toHijri's local getters read the same calendar day in every zone.
 */
function hijriOf(date, offsetOf = () => 0) {
  const [y, m, d] = String(date).split('-').map(Number);
  const h = H.toHijri(new Date(y, m - 1, d, 12), Number(offsetOf(date)) || 0);
  return { year: h.year, month: h.month, day: h.day };
}

/**
 * The Ramadan that BEGINS in Gregorian `year` (1 Ramadan falls in it), by the mosque's calendar: { hijriYear, days }.
 * null when no Ramadan begins in that year by this calendar (a Gregorian year can hold two; the earlier is taken —
 * the staff can still print the other with a day range).
 */
function ramadanDays(year, offsetOf = () => 0) {
  const y = Number(year);
  if (!Number.isInteger(y) || y < 1900 || y > 2200) return null;
  const last = `${y}-12-31`;
  for (let d = `${y}-01-01`; d <= last; d = addDays(d, 1)) {
    const h = hijriOf(d, offsetOf);
    if (h.month === 9 && h.day === 1) {
      const days = [];
      for (let x = d; days.length < 31; x = addDays(x, 1)) {
        if (hijriOf(x, offsetOf).month !== 9) break;
        days.push(x);
      }
      return { hijriYear: h.year, days };
    }
  }
  return null;
}

/**
 * One row per date: { date, weekday, hijri, closed, begins, jamaat }. `dayOf(date)` is the resolved day as every
 * surface reads it (serialize.prayerDay: { begins, jamaat, isClosed }) or null; `hijriFor(date)` the mosque's Hijri
 * date ({ day, month, year }) or null. A date with no timetable row has begins/jamaat null — never filled in.
 */
function rows(dates, { dayOf = () => null, hijriFor = () => null } = {}) {
  return (dates || []).map((date) => {
    const day = dayOf(date) || null;
    const h = hijriFor(date) || null;
    const pick = (o, keys) => (o ? Object.fromEntries(keys.map((k) => [k, typeof o[k] === 'string' && /^\d{1,2}:\d{2}$/.test(o[k]) ? o[k] : null])) : null);
    return {
      date, weekday: weekdayOf(date),
      hijri: h ? { day: Number(h.day), month: Number(h.month), year: Number(h.year) } : null,
      closed: Boolean(day && day.isClosed),
      begins: day ? pick(day.begins, PRAYERS) : null,
      jamaat: day ? pick(day.jamaat, ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha']) : null,
    };
  });
}

/** A prayer's cell: when it begins, and the jamaat in brackets where one is set and differs; '–' without a time. */
function cell(row, prayer) {
  const b = row.begins && row.begins[prayer];
  if (!b) return DASH;
  const j = row.jamaat && row.jamaat[prayer];
  return j && j !== b ? `${b} (${j})` : b;
}

const dm = (date) => `${date.slice(8, 10)}.${date.slice(5, 7)}`;
const SHORT_HIJRI = ['Muh', 'Saf', 'Rab I', 'Rab II', 'Jum I', 'Jum II', 'Raj', 'Sha', 'Ram', 'Shaw', 'Dhul-Q', 'Dhul-H'];

/**
 * The table: { columns: [{ key, label, weight, align }], body: [{ date, cells: [string], friday, closed, oddNight }] }.
 * `weight` is a column's share of the width (the PDF sizes by it). Ramadan: the Ramadan day, the date, the weekday,
 * sehri (= Fajr begins, never the jamaat), the prayers, Maghrib named Iftar. Month: the date, the weekday, the Hijri
 * date, the prayers.
 */
function table(rowList, { kind = 'month', lang = 'no', imsakOffsetMin = 0 } = {}) {
  // Imsak (the Ramadan module, 2026-10-31): the mosque may end sehri N minutes before Fajr begins; the printed sehri
  // column says that time, as the app and the screens do (core ramadan.minusMinutes).
  const sehriOf = (r) => { const f = r.begins && r.begins.fajr; return f ? (imsakOffsetMin > 0 ? require('./ramadan').minusMinutes(f, imsakOffsetMin) || f : f) : DASH; };
  const L = LABELS[lang] || LABELS.no;
  const c = L.col;
  const prayerCols = [['fajr', c.fajr], ['sunrise', c.sunrise], ['dhuhr', c.dhuhr], ['asr', c.asr],
    [kind === 'ramadan' ? 'iftar' : 'maghrib', kind === 'ramadan' ? `${c.iftar}/${c.maghrib}` : c.maghrib], ['isha', c.isha]]
    .map(([key, label]) => ({ key, label, weight: key === 'sunrise' ? 1.1 : 1.45, align: 'center' }));
  const columns = kind === 'ramadan'
    ? [{ key: 'rday', label: c.rday, weight: 0.55, align: 'center' }, { key: 'date', label: c.date, weight: 0.7, align: 'left' },
       { key: 'weekday', label: c.weekday, weight: 0.75, align: 'left' }, { key: 'sehri', label: c.sehri, weight: 1.05, align: 'center' }, ...prayerCols]
    : [{ key: 'date', label: c.date, weight: 0.7, align: 'left' }, { key: 'weekday', label: c.weekday, weight: 0.75, align: 'left' },
       { key: 'hijri', label: c.hijri, weight: 1.05, align: 'left' }, ...prayerCols];
  const body = (rowList || []).map((r) => {
    const wd = L.weekdays[r.weekday];
    const valueOf = (key) => {
      if (key === 'rday') return r.hijri && r.hijri.month === 9 ? String(r.hijri.day) : DASH;
      if (key === 'date') return dm(r.date);
      if (key === 'weekday') return wd;
      if (key === 'hijri') return r.hijri ? `${r.hijri.day} ${SHORT_HIJRI[r.hijri.month - 1]}` : DASH;
      if (key === 'sehri') return sehriOf(r);
      if (key === 'iftar') return cell(r, 'maghrib');
      return cell(r, key);
    };
    return { date: r.date, cells: columns.map((col) => valueOf(col.key)), friday: r.weekday === 4, closed: r.closed,
             oddNight: Boolean(r.hijri && r.hijri.month === 9 && [21, 23, 25, 27, 29].includes(r.hijri.day)) };
  });
  return { columns, body };
}

/** The mosque's public line under the title: name, city, website. Nothing about a person. */
function mosqueLine(m) {
  if (!m) return '';
  return [m.name, m.city, m.website].filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim()).join(' · ');
}

/**
 * Everything one printout says: { kind, lang, title, subtitle, columns, body, notes, footer, from, to, missing }.
 *   kind 'ramadan': `hijriYear` + `year` name it;  kind 'month': `year` + `month` do.
 * `printedOn` is a bare date the caller passes (no clock here). `missing` counts the days without a timetable row, so
 * the panel can say so before anything is printed. `source` is the IRN credit ({ name, url }) when the mosque's times
 * come from IRN: every surface that shows the times names it (2026-10-14), a printout too.
 */
function document({ kind = 'month', lang = 'no', rows: rowList = [], mosque = null, year = null, month = null, hijriYear = null, printedOn = null, source = null, imsakOffsetMin = 0 } = {}) {
  const k = KINDS.includes(kind) ? kind : 'month';
  const l = LANGS.includes(lang) ? lang : 'no';
  const L = LABELS[l];
  const t = table(rowList, { kind: k, lang: l, imsakOffsetMin: k === 'ramadan' ? Number(imsakOffsetMin) || 0 : 0 });
  const first = rowList.length ? rowList[0].date : null, last = rowList.length ? rowList[rowList.length - 1].date : null;
  const title = k === 'ramadan'
    ? L.ramadanTitle(hijriYear || (rowList[0] && rowList[0].hijri ? rowList[0].hijri.year : ''), year || (first ? first.slice(0, 4) : ''))
    : L.monthTitle(L.months[(Number(month) || 1) - 1], year);
  const missing = rowList.filter((r) => !r.begins).length;
  const credit = source && source.name && source.url ? L.source(source.name, String(source.url).replace(/^https?:\/\//, '')) : null;
  const notes = [L.jamaatNote, ...(k === 'ramadan' ? [L.sehriNote, L.hijriNote] : []), L.missingNote,
    ...(rowList.some((r) => r.closed) ? [L.closedNote] : []), ...(credit ? [credit] : [])];
  const footer = [mosqueLine(mosque), printedOn ? L.printed(printedOn) : ''].filter(Boolean).join(' · ');
  return { kind: k, lang: l, title, subtitle: mosqueLine(mosque), columns: t.columns, body: t.body, notes, footer,
           from: first, to: last, missing, source: credit ? { provider: source.provider || null, name: source.name, url: source.url } : null };
}

/**
 * Posters to print (MKK #34): a banner drawn by the panel on a print-sized canvas, with core social-share.layout() — the
 * same layout as «Del», at A4 or A3 portrait, 200 dpi. `text` is the multiplier layout() applies to the banner's text
 * sizes (percent of the width), between the portrait (1.3) and the story (1.4) share sizes, as A4 sits between them.
 * `ground` is the paper behind a picture banner: white, not the screens' dark ground, so a printer is not asked for a
 * page of black ink.
 */
const POSTER_SIZES = {
  a4: { id: 'a4', w: 1654, h: 2339, text: 1.35, mm: [210, 297], label_no: 'A4 (stående)' },
  a3: { id: 'a3', w: 2339, h: 3307, text: 1.35, mm: [297, 420], label_no: 'A3 (stående)' },
};
const POSTER_ORDER = ['a4', 'a3'];
const POSTER_GROUND = '#ffffff';

/** A poster's file name: plakat-<title>-a4.png — ASCII, no person. */
function posterFileName(title, sizeId) {
  const s = POSTER_SIZES[sizeId] ? sizeId : 'a4';
  const slug = String(title || '').toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/å/g, 'a')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
  return `plakat${slug ? '-' + slug : ''}-${s}.png`;
}

module.exports = { PRAYERS, DASH, MAX_DAYS, KINDS, LANGS, LABELS, EXTRA_HEADINGS, SHORT_HIJRI,
  isDate, addDays, weekdayOf, daysBetween, monthDays, hijriOf, ramadanDays, rows, cell, table, mosqueLine, document,
  POSTER_SIZES, POSTER_ORDER, POSTER_GROUND, posterFileName };
